import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import FeatureBranch from '@/common/FeatureBranch';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import Alerts from '@/vdp/Alerts';
import BreadCrumbs from '@/vdp/BreadCrumbs';
import CarCompareLite from '@/vdp/CarCompareLite';
import DeliveryUnavailableBanner from '@/vdp/DeliveryUnavailableBanner';
import { HeroContextProvider } from '@/vdp/Hero/HeroContext';
import HeroHeaderLite from '@/vdp/HeroHeaderLite/HeroHeaderLite';
import KeyFeatures from '@/vdp/KeyFeatures';
import LiteGallery from '@/vdp/LiteGallery/LiteGallery';
import DeliveryAndPickupLite from '@/vdp/LiteTextBoxes';
import PriceDetails from '@/vdp/PriceDetails';
import { useShowKBBPromo } from '@/vdp/StickyBanner/hooks';
import Sections from '~/constants/sections';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useGetShowEVTaxTreatment, usePriceDrop } from '~/hooks/vdp';
import useShouldShowStatusData from '~/hooks/vdp/useShouldShowStatusData';
import {
  getCtaButtonState,
  getIsDealerMarketplace,
  getIsNotAvailableForImmediatePurchase
} from '~/selectors';
import SectionsWrapper from './SectionsWrapper';
import { HeroContainer, Wrapper } from './VehicleDetailsContainer.styles';

const AboutThePrice = dynamic(
  () => import(/* webpackChunkName: "AboutThePrice" */ '@/vdp/AboutThePrice')
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  { ssr: false }
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const OnHoldBanner = dynamic(
  () => import(/* webpackChunkName: "OnHoldBanner" */ '@/vdp/OnHoldBanner'),
  { ssr: false }
);

const StatusBanner = dynamic(
  () => import(/* webpackChunkName: "StatusBanner" */ '@/vdp/StatusBanner'),
  { ssr: false }
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const StickyCtaLite = dynamic(
  () => import(/* webpackChunkName: "StickyCtaLite" */ '@/vdp/StickyCtaLite')
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  { ssr: false }
);

const One50PointInspection = dynamic(
  () =>
    import(/* webpackChunkName: "One50PointInspection" */ '@/vdp/Inspection'),
  { ssr: false }
);

const OwnerReviews = dynamic(
  () => import(/* webpackChunkName: "OwnerReviews" */ '@/vdp/OwnerReviews'),
  { ssr: false }
);

const AncillaryProducts = dynamic(
  () =>
    import(
      /* webpackChunkName: "AncillaryProducts" */ 'src/components/vdp/AncillaryProducts'
    ),
  { ssr: false }
);

const BuyWithConfidence = dynamic(
  () =>
    import(
      /* webpackChunkName: "BuyWithConfidence" */ '@/vdp/BuyWithConfidence'
    ),
  { ssr: false }
);

const VehicleDetailsLiteContainer = () => {
  const { notifyMe } = useSelector(getCtaButtonState);
  const isMarketplace = useSelector(getIsDealerMarketplace);
  const prioritizeMCYML = useSelector(getIsNotAvailableForImmediatePurchase);
  const shouldShowStatusData = useShouldShowStatusData();
  const showEVTaxTreatment = useGetShowEVTaxTreatment();
  const isMobile = useIsMobile();
  const showKBBPromo = useShowKBBPromo();
  const { showPriceDrop } = usePriceDrop();

  const showAboutPrice = showPriceDrop || showKBBPromo || showEVTaxTreatment;
  const showStickyCtaLite = isMobile && (!notifyMe || shouldShowStatusData);

  return (
    <>
      <FeatureBranch appName="merchui" />
      <Wrapper id={Sections.Root} isMarketplace={isMarketplace}>
        <PageHeader />
        <SectionsWrapper>
          <StickyBanner />
          <BreadCrumbs />
          <OnHoldBanner />
          <DeliveryUnavailableBanner />
          <StatusBanner />
          <HeroContextProvider>
            <HeroContainer>
              <HeroHeaderLite />
              <div className="relative">
                <Alerts />
                <LiteGallery />
              </div>
            </HeroContainer>
          </HeroContextProvider>
          {prioritizeMCYML && <CarCompareLite />}
          <DeliveryAndPickupLite />
          {showAboutPrice && <AboutThePrice />}
          {!prioritizeMCYML && <CarCompareLite />}
          <PriceDetails whiteBg />
          <KeyFeatures />
          <VehicleDetails />
          <One50PointInspection />
          <BuyWithConfidence />
          <AncillaryProducts />
          <OwnerReviews />
        </SectionsWrapper>
        {showStickyCtaLite && <StickyCtaLite />}
        <Disclaimer />
      </Wrapper>
      <PageFooter />
      <Modals />
    </>
  );
};

export default VehicleDetailsLiteContainer;
